import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { isAdminUser } from "../lib/admin";
import { fetchFunnelSummary, type FunnelSummary } from "../lib/funnel";

const POLL_MS = 15000;

function formatTypeLabel(type: string) {
  return type.replace(/_/g, " ");
}

const AdminUserRadar: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [summary, setSummary] = useState<FunnelSummary | null>(null);
  const [flash, setFlash] = useState(false);
  const totalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      setSummary(null);
      totalRef.current = null;
      return;
    }

    let cancelled = false;
    let flashTimer: number | undefined;

    const load = async () => {
      try {
        const next = await fetchFunnelSummary();
        if (cancelled) return;
        if (totalRef.current !== null && next.total > totalRef.current) {
          setFlash(true);
          flashTimer = window.setTimeout(() => setFlash(false), 6000);
        }
        totalRef.current = next.total;
        setSummary(next);
      } catch {
        if (!cancelled) {
          setSummary(null);
        }
      }
    };

    void load();
    const intervalId = window.setInterval(() => {
      void load();
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (flashTimer) {
        window.clearTimeout(flashTimer);
      }
    };
  }, [isAdmin]);

  if (!isAdmin || !summary) {
    return null;
  }

  return (
    <Link
      to="/admin"
      data-testid="admin-user-radar"
      className={`fixed right-4 top-20 z-[9998] w-72 rounded-2xl border px-4 py-4 shadow-2xl backdrop-blur transition-all ${
        flash
          ? "border-emerald-300/80 bg-emerald-500/20 shadow-emerald-500/20 animate-pulse"
          : "border-amber-300/30 bg-slate-950/90 hover:border-amber-200/50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em] text-amber-200/80">
            User Radar
          </div>
          <div className="mt-2 text-3xl font-black text-white">
            {summary.last24h}
          </div>
          <div className="text-xs text-slate-300">
            events in the last 24 hours
          </div>
        </div>
        <div className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-300">
          Live
        </div>
      </div>

      {summary.latest && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
            Latest
          </div>
          <div className="mt-1 text-sm font-semibold text-white break-all">
            {summary.latest.email}
          </div>
          <div className="mt-1 text-xs text-slate-400">
            {formatTypeLabel(summary.latest.type)} ·{" "}
            {summary.latest.route || summary.latest.source || "unknown"}
          </div>
        </div>
      )}
    </Link>
  );
};

export default AdminUserRadar;
